import * as React from 'react';

import {
  CalciteActionBar,
  CalciteActionGroup,
  CalciteIcon,
  CalcitePanel,
  CalcitePopover,
  CalciteShell,
  CalciteShellPanel,
} from '@esri/calcite-components-react';

import {
  getLayer,
  getServiceAdminInfo,
  parseServiceUrl,
} from '@esri/arcgis-rest-feature-service';
import { useQuery } from '@tanstack/react-query';

import ProfileComponent from './ProfileComponent';
import ItemBrowser from './ItemBrowser/ItemBrowser';
import TableManager from './Table/TableManager';

import Header from './Layouts/Header';
import ItemSearch from './ItemBrowser/ItemSearch';
import AppInfoPanel from './Panels/AppInfoPanel';
import usePanelState from '../hooks/usePanelState';
import { useSearchParams } from 'react-router-dom';
import { TableStateProvider } from '../contexts/TableContext';
import { CustomCalciteAction } from './CustomCalciteAction';
import { useMemo } from 'react';
import { toast } from 'react-toastify';
import { useAccountManager } from '../contexts/AccountManagerContext';
import { RecentPanel } from './Panels/RecentPanel';
import { ArcGISAuthError, ArcGISRequestError } from '@esri/arcgis-rest-request';

import AppSettingsPanel from './Panels/AppSettingsPanel';
type PrimaryPanel =
  | 'Search'
  | 'Info'
  | 'Settings'
  | 'Add Layer'
  | 'Collapsed'
  | 'Recent';

export default function App(): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams({ url: '' });
  const url = React.useMemo(
    () => searchParams.get('url') ?? '',
    [searchParams]
  );

  const [primaryPanel, setPrimaryPanel] =
    usePanelState<PrimaryPanel>('Collapsed');

  // Authentication options
  const { accountManager } = useAccountManager();

  const isValidUrl = useMemo(() => {
    const parsedUrl = parseServiceUrl(url);
    const isValid = parsedUrl !== url;
    //show toast warning
    if (url !== '' && !isValid) {
      toast.warn('Please ensure the URL is a valid ArcGIS Feature Layer.');
    } else {
      toast.dismiss();
    }
    return isValid;
  }, [url]);

  const {
    error: layerDefinitionError,
    data: layerDefinition,
    refetch: refreshTableData,
  } = useQuery({
    queryKey: ['layerDefinition', url, accountManager],
    enabled: !!url && isValidUrl,
    retry: false,
    queryFn: async () => {
      const layer = await getLayer({
        url,
        authentication: accountManager,
      });
      if (Object.hasOwn(layer, 'layers')) {
        throw new Error(
          'Provided URL is a map service. Please provide a URL to a feature layer.'
        );
      }

      return layer;
    },
  });

  const { data: isAdminMode } = useQuery({
    queryKey: ['serviceAdminInfo', url, accountManager],
    enabled: !!url && !!accountManager,
    queryFn: async () => {
      if (!accountManager) return false;
      try {
        const adminInfo = await getServiceAdminInfo(url, accountManager);
        return !!adminInfo;
      } catch (error) {
        if (error instanceof ArcGISRequestError && error.code === 403) {
          return false;
        }
        throw error;
      }
    },
    retry: false,
    initialData: false,
  });

  return (
    <CalciteShell id="app-node" className="calcite-mode-dark app-theme">
      <header
        slot="header"
        style={{
          display: 'flex',
          height: 55,
          justifyContent: 'space-between',
        }}
      >
        <Header layerDefinition={layerDefinition} url={url} />

        <span className="flex items-center gap-4 px-2">
          <ProfileComponent layerUrl={url} />
        </span>
      </header>

      {/* Left side action bar and panel */}
      <CalciteShellPanel
        slot="panel-start"
        position="start"
        detachedHeightScale="l"
        widthScale="m"
        collapsed={primaryPanel === 'Collapsed' || primaryPanel === 'Add Layer'}
      >
        <CalciteActionBar slot="action-bar">
          <CalciteActionGroup>
            <CustomCalciteAction
              selectedPanel={primaryPanel}
              panelName="Add Layer"
              icon="add-layer"
              tooltip="Add a layer by URL"
              onClick={setPrimaryPanel}
              id="addLayerButton"
            />
            <CustomCalciteAction
              selectedPanel={primaryPanel}
              panelName="Search"
              icon="search"
              tooltip="Search for a layer"
              onClick={setPrimaryPanel}
            />
            <CustomCalciteAction
              selectedPanel={primaryPanel}
              panelName="Recent"
              icon="recent"
              tooltip="Recent layers"
              onClick={setPrimaryPanel}
            />
          </CalciteActionGroup>
          <CalciteActionGroup slot="bottom-actions">
            <CustomCalciteAction
              selectedPanel={primaryPanel}
              panelName="Settings"
              icon="gear"
              tooltip="App Settings"
              onClick={setPrimaryPanel}
            />
            <CustomCalciteAction
              selectedPanel={primaryPanel}
              panelName="Info"
              icon="question"
              tooltip="App Info"
              onClick={setPrimaryPanel}
            />
          </CalciteActionGroup>
        </CalciteActionBar>

        {/* Search Panel */}
        <CalcitePanel hidden={primaryPanel !== 'Recent'}>
          {primaryPanel === 'Recent' && (
            <RecentPanel
              url={url}
              onLayerClick={(newUrl) => setSearchParams({ url: newUrl })}
              layerDefinition={layerDefinition}
            />
          )}
        </CalcitePanel>

        {/* Search Panel */}
        <CalcitePanel hidden={primaryPanel !== 'Search'}>
          {primaryPanel === 'Search' && (
            <ItemBrowser
              addLayer={(newLayer) => {
                setSearchParams({ url: newLayer.url });
              }}
              currentUrl={url}
            />
          )}
        </CalcitePanel>

        {/* Settings Panel */}
        <CalcitePanel heading="Settings" hidden={primaryPanel !== 'Settings'}>
          <AppSettingsPanel />
        </CalcitePanel>

        {/* Info Panel */}
        <CalcitePanel heading="About" hidden={primaryPanel !== 'Info'}>
          <AppInfoPanel />
        </CalcitePanel>
      </CalciteShellPanel>

      {/* Show table only when layer is initialized */}

      {layerDefinition && url && (
        <TableStateProvider>
          <TableManager
            layerDef={layerDefinition}
            url={url}
            refreshTableData={refreshTableData}
            isAdminMode={isAdminMode}
          />
        </TableStateProvider>
      )}

      {layerDefinitionError instanceof Error && (
        <div className="flex-1 flex justify-center items-center">
          <CalciteIcon icon="frown" scale="l" className="pr-2 animate-bounce" />
          <span className="text-2">
            {layerDefinitionError instanceof ArcGISAuthError &&
            layerDefinitionError.code === 'GWM_0003'
              ? 'Please check if you are logged in and have access to this layer'
              : layerDefinitionError.message}
          </span>
        </div>
      )}
      {/* Popover to enter URL to layer */}
      <CalcitePopover
        referenceElement="addLayerButton"
        label="Add Layer by Url"
        open={primaryPanel === 'Add Layer'}
        overlayPositioning="fixed"
        placement="right"
      >
        <div className="w-[500px]">
          {/* Search component to get a url */}
          <ItemSearch
            onSubmitSearch={(query) => {
              setSearchParams({ url: query.url ?? '' });
              setPrimaryPanel('Collapsed');
            }}
            placeholder="Input a URL to a Feature Layer"
            initialValue={url ?? undefined}
            icon="send"
          />
        </div>
      </CalcitePopover>
    </CalciteShell>
  );
}
